import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Card, Table, Image, Modal } from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css'; 

const Banner = () => {
    const [bannerData, setBannerData] = useState({
        id: null,
        title: "",
        desc: "",
        price: "",
        image: null
    });
    const [banners, setBanners] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [errors, setErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null); // For image preview
    const imagePath = "https://api.biznetusa.com/uploads/banners/";

    useEffect(() => {
        const fetchBanners = async () => {
            try {
                const response = await fetch("https://api.biznetusa.com/api/get-banners");
                const data = await response.json();
                if (data.status === 200) {
                    setBanners(data.banners);
                }
            } catch (error) {
                console.error("Error fetching banners:", error);
            }
        };

        fetchBanners();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBannerData({ ...bannerData, [name]: value });
        setErrors({ ...errors, [name]: null });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setBannerData({ ...bannerData, image: file });
            setImagePreview(URL.createObjectURL(file)); // Set image preview
        }
    };

    const handleRemoveImage = () => {
        setBannerData({ ...bannerData, image: null });
        setImagePreview(null); // Reset image preview
    };

    const handleShowModal = () => setShowModal(true);
    const handleCloseModal = () => {
        setShowModal(false);
        setIsEdit(false);
        setBannerData({ title: "", desc: "", price: "", image: null });
        setImagePreview(null); // Reset preview
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("title", bannerData.title);
        formData.append("desc", bannerData.desc);
        formData.append("price", bannerData.price);
        if (bannerData.image) {
            formData.append("image", bannerData.image);
        }

        try {
            let url = "https://api.biznetusa.com/api/store-banner";
            let method = "POST";
            if (isEdit && bannerData.id) {
                url = `https://api.biznetusa.com/api/update-banner/${bannerData.id}`;
                method = "PUT";
            }

            const response = await fetch(url, {
                method,
                body: formData,
            });

            const result = await response.json();

            if (response.ok) {
                toast.success(isEdit ? "Banner updated successfully" : "Banner uploaded successfully");
                setBannerData({ title: "", desc: "", price: "", image: null });
                setErrors({});
                if (isEdit) {
                    setBanners(banners.map(b => (b.id === result.banners[0].id ? result.banners[0] : b)));
                } else {
                    setBanners([...banners, result.banners[0]]);
                }
                handleCloseModal();
            } else {
                if (result.errors) {
                    setErrors(result.errors);
                } else {
                    toast.error("Error uploading banner");
                }
            }
        } catch (error) {
            console.error("Error uploading banner:", error);
            toast.error("An error occurred while uploading the banner.");
        }
    };

    const handleEdit = (banner) => {
        setIsEdit(true);
        setBannerData({
            id: banner.id,
            title: banner.title,
            desc: banner.desc,
            price: banner.price,
            image: null
        });
        setImagePreview(`${imagePath}${banner.image}`); // Set image preview for editing
        handleShowModal();
    };

    const handleDelete = async (bannerId) => {
        if (window.confirm("Are you sure you want to delete this banner?")) {
            try {
                const response = await fetch(`https://api.biznetusa.com/api/deletebanner/${bannerId}`, {
                    method: "DELETE",
                });

                if (response.ok) {
                    toast.success("Banner deleted successfully");
                    setBanners(banners.filter(b => b.id !== bannerId));
                } else {
                    toast.error("Error deleting banner");
                }
            } catch (error) {
                console.error("Error deleting banner:", error);
                toast.error("An error occurred while deleting the banner.");
            }
        }
    };

    return (
        <>
            <Container fluid className="py-4">
                <Row>
                    <Col md={10} className="mx-auto">
                        <Card className="shadow-sm p-4 border-0 rounded-lg">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h4 className="mb-0 text-primary">Manage Banners</h4>
                                <Button variant="success" onClick={handleShowModal}>
                                    Add New Banner
                                </Button>
                            </div>
                            <Table striped bordered hover responsive className="text-center">
                                <thead className="thead-dark">
                                    <tr>
                                        <th>ID</th>
                                        <th>Title</th>
                                        <th>Description</th>
                                        <th>Price</th>
                                        <th>Image</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {banners.map((banner) => (
                                        <tr key={banner.id}>
                                            <td>{banner.id}</td>
                                            <td>{banner.title}</td>
                                            <td>{banner.desc}</td>
                                            <td>${banner.price}</td>
                                            <td>
                                                {banner.image ? (
                                                    <Image
                                                        src={`${imagePath}${banner.image}`}
                                                        alt={banner.title}
                                                        width={80}
                                                        rounded
                                                    />
                                                ) : (
                                                    <span className="text-muted">No Image</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button variant="warning" className="mx-1" onClick={() => handleEdit(banner)}>
                                                    Edit
                                                </Button>
                                                <Button variant="danger" className="mx-1" onClick={() => handleDelete(banner.id)}>
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Modal for Adding/Editing Banner */}
            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? "Edit Banner" : "Add New Banner"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group controlId="title">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={bannerData.title}
                                onChange={handleChange}
                                placeholder="Enter banner title"
                                isInvalid={!!errors.title}
                            />
                            {errors.title && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.title[0]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group controlId="desc" className="mt-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                name="desc"
                                value={bannerData.desc}
                                onChange={handleChange}
                                placeholder="Enter banner description"
                                rows={3}
                                isInvalid={!!errors.desc}
                            />
                            {errors.desc && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.desc[0]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group controlId="price" className="mt-3">
                            <Form.Label>Price ($)</Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={bannerData.price}
                                onChange={handleChange}
                                placeholder="Enter price"
                                isInvalid={!!errors.price}
                            />
                            {errors.price && (
                                <Form.Control.Feedback type="invalid">
                                    {errors.price[0]}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group controlId="image" className="mt-3">
                            <Form.Label>Banner Image</Form.Label>
                            <Form.Control
                                type="file"
                                name="image"
                                onChange={handleFileChange}
                            />
                            {imagePreview && (
                                <div className="mt-3 position-relative">
                                    <Image
                                        src={imagePreview}
                                        alt="Preview"
                                        width={100}
                                        rounded
                                    />
                                    <span
                                        className="position-absolute top-0 start-100 translate-middle p-2 bg-danger text-white rounded-circle"
                                        style={{ cursor: 'pointer' }}
                                        onClick={handleRemoveImage}
                                    >
                                        &times;
                                    </span>
                                </div>
                            )}
                        </Form.Group>

                        <Button type="submit" variant="primary" className="mt-4 w-100">
                            {isEdit ? "Update Banner" : "Upload Banner"}
                        </Button>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Toast Container */}
            <ToastContainer />
        </>
    );
};

export default Banner;
